/**
 * GSAP Slide Type Definitions
 * Defines the structure of GSAP-based slide JSON files
 */

export interface GsapAnimation {
  target: string;
  type: 'set' | 'to' | 'from' | 'fromTo';
  props: Record<string, any>;
  position?: number | string;
  duration?: number;
  ease?: string;
}

export interface GsapLayer {
  type: 'gsap' | 'video' | 'audio';
  html?: string;
  css?: string;
  gsapAnimations?: GsapAnimation[];
  videoSrc?: string;
  videoConfig?: {
    startTime?: number;
    endTime?: number;
    playbackRate?: number;
    loop?: boolean;
    muted?: boolean;
    volume?: number;
  };
  audioSrc?: string;
  audioConfig?: {
    volume?: number;
    loop?: boolean;
    fadeIn?: number;
    fadeOut?: number;
  };
}

export interface GsapSlideData {
  id: string;
  layers: GsapLayer[];
  duration?: number;
  // Fields can be at root level (from info.json) or nested under metadata
  title?: string;
  scene_id?: string;
  layout_type?: string;
  script_text?: string;
  content_description?: string;
  visual_instructions?: string;
  estimated_duration?: number;
  start_time?: number;
  end_time?: number;
  metadata?: {
    title?: string;
    description?: string;
    layout_type?: string;
    scene_id?: string;
    estimated_duration?: number;
    script_text?: string;
    content_description?: string;
    visual_instructions?: string;
    start_time?: number;
    end_time?: number;
  };
}

/**
 * Unified slide type that can be either HTML or GSAP
 */
export type SlideFormat = 'html' | 'gsap';

export interface UnifiedSlide {
  id: string;
  number: string;
  title: string;
  path: string;
  format: SlideFormat;
  content?: string; // For HTML slides
  gsapData?: GsapSlideData; // For GSAP slides
  duration?: number;
  layoutType?: string;
  // Database fields returned by slide serve API
  databaseId?: string; // Database UUID for audio/version API calls
  slideIdentifier?: string; // Path-based identifier (e.g., "slide-001")
  currentVersion?: number; // Current version number from SlideVersion
  metadata?: {
    scene_id?: string;
    script_text?: string;
    content_description?: string;
    visual_instructions?: string;
    estimated_duration?: number;
    start_time?: number;
    end_time?: number;
    layout_type?: string;
    // Template-specific fields
    isTemplate?: boolean;
    templateId?: string;
    slideJsonUrl?: string;
  };
}

/**
 * Extract metadata from GSAP slide data
 * Checks root level first (where info.json stores data), then falls back to nested metadata
 */
export function extractGsapMetadata(gsapData: GsapSlideData | null | undefined): UnifiedSlide['metadata'] {
  // Handle null/undefined data
  if (!gsapData) {
    return {};
  }

  try {
    return {
      scene_id: gsapData.scene_id || gsapData.metadata?.scene_id || gsapData.id,
      script_text: gsapData.script_text || gsapData.metadata?.script_text,
      content_description: gsapData.content_description || gsapData.metadata?.content_description,
      visual_instructions: gsapData.visual_instructions || gsapData.metadata?.visual_instructions,
      estimated_duration: gsapData.estimated_duration || gsapData.metadata?.estimated_duration || gsapData.duration,
      start_time: gsapData.start_time || gsapData.metadata?.start_time,
      end_time: gsapData.end_time || gsapData.metadata?.end_time,
    };
  } catch (error) {
    console.warn('Error extracting GSAP metadata:', error);
    return {};
  }
}

/**
 * Extract title from GSAP slide data
 */
export function extractGsapTitle(gsapData: GsapSlideData | null | undefined): string {
  // Handle null/undefined data
  if (!gsapData) {
    return 'Untitled Slide';
  }

  // Check root-level title first (from info.json merged by backend)
  if (gsapData.title) {
    return gsapData.title;
  }

  // Check title from metadata
  if (gsapData.metadata?.title) {
    return gsapData.metadata.title;
  }

  // Check if layers exist before trying to find
  if (!gsapData.layers || !Array.isArray(gsapData.layers) || gsapData.layers.length === 0) {
    return 'Untitled Slide';
  }

  // Try to extract from HTML content if available
  try {
    const firstGsapLayer = gsapData.layers.find(l => l && l.type === 'gsap' && l.html);
    if (firstGsapLayer?.html) {
      const titleMatch = firstGsapLayer.html.match(/<title>(.*?)<\/title>/i);
      if (titleMatch && titleMatch[1]) {
        return titleMatch[1].replace(/^Slide \d+:\s*/, '');
      }

      const h1Match = firstGsapLayer.html.match(/<h1[^>]*>(.*?)<\/h1>/i);
      if (h1Match && h1Match[1]) {
        return h1Match[1];
      }
    }
  } catch (error) {
    console.warn('Error extracting title from GSAP slide:', error);
  }

  return 'Untitled Scene';
}

/**
 * Calculate total duration from GSAP animations
 */
export function calculateGsapDuration(gsapData: GsapSlideData | null | undefined): number {
  // Handle null/undefined data
  if (!gsapData) {
    return 5; // Default duration
  }

  // Try to get duration from various sources
  if (typeof gsapData.duration === 'number' && gsapData.duration > 0) {
    return gsapData.duration;
  }

  if (typeof gsapData.metadata?.estimated_duration === 'number' && gsapData.metadata.estimated_duration > 0) {
    return gsapData.metadata.estimated_duration;
  }

  // Check if layers exist before calculating
  if (!gsapData.layers || !Array.isArray(gsapData.layers) || gsapData.layers.length === 0) {
    return 5; // Default duration
  }

  // Calculate from animations
  let maxDuration = 0;

  try {
    for (const layer of gsapData.layers) {
      if (!layer) continue;

      // Check GSAP animations
      if (layer.gsapAnimations && Array.isArray(layer.gsapAnimations)) {
        for (const anim of layer.gsapAnimations) {
          if (!anim) continue;
          const duration = typeof anim.duration === 'number' ? anim.duration : 0;
          const position = typeof anim.position === 'number' ? anim.position : 0;
          const animDuration = duration + position;
          maxDuration = Math.max(maxDuration, animDuration);
        }
      }

      // Check video config
      if (layer.videoConfig?.endTime && typeof layer.videoConfig.endTime === 'number') {
        maxDuration = Math.max(maxDuration, layer.videoConfig.endTime);
      }
    }
  } catch (error) {
    console.warn('Error calculating GSAP duration:', error);
  }

  return maxDuration > 0 ? maxDuration : 5; // Default to 5 seconds if can't calculate
}
